import React, { useMemo, useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  useExpanded,
  useGlobalFilter,
  usePagination,
} from "react-table";
import Spinner from "../common/Spinner";
import SubReportTable from "./SubReportTable";
import { MAIN_COLUMNS, generateData } from "./utils";
import { Button } from "devextreme-react";
import GlobalFiter from "./GlobalFiter";
import Pagination from "../common/Pagination";
import VerificationModal from "./VerificationModal";
import { requestERPLYData } from "../../api/Server";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { Toast } from "devextreme-react/toast";
import { ReactComponent as ExpandIcon } from "../../assets/icon/maximize-2.svg";
import { ReactComponent as MinimizeIcon } from "../../assets/icon/minimize-2.svg";

const DistributionTable = ({
  reportStore,
  isLoading,
  user,
  getReport,
  reportType,
}) => {
  const [mainData, setMain] = useState([]);
  const [generatedData, setData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [newVersionAvailable, setNewVersion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const columns = useMemo(() => MAIN_COLUMNS, []);
  const data = useMemo(() => reportStore, [reportStore]);
  const initialExpanded = useMemo(() => {
    const expanded = {};
    reportStore.forEach((e, i) => {
      expanded[i] = true;
    });
    return {
      expanded,
    };
  }, [reportStore]);

  const closeModal = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    setIsOpen(true);
    setData(generateData(mainData, reportType));
  };

  useEffect(() => {
    setMain(reportStore);
  }, [reportStore]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    prepareRow,
    visibleColumns,
    toggleAllRowsExpanded,
    state: { globalFilter, pageIndex, pageSize },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { ...initialExpanded, pageSize: 50 },
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  const createTransfers = () => {
    const axioArray = [];
    if (generatedData && generatedData.requestData.length) {
      setIsSaving(true);

      for (const data of generatedData.requestData) {
        delete data["lastCount"];
        const requestData = {
          ...data,
          clientCode: user ? user.clientCode : "",
          creatorID: user ? user.employeeID : 0,
          request: "saveInventoryTransfer",
        };

        if (user && (user.isIframe || user.sessionKey)) {
          requestData.sessionKey = user.sessionKey;
        }

        axioArray.push(requestERPLYData(requestData));
      }

      axios
        .all(axioArray)
        .then(
          axios.spread((...responses) => {
            responses.forEach((res) => {
              if (res.data.status.responseStatus === "error") {
                notify(
                  `Error creating transfers. Please try again`,
                  "error",
                  3000
                );

                setIsSaving(false);
                setNewVersion(false);
                return;
              } else {
                setIsSaving(false);
                closeModal();
                setNewVersion(true);
              }
            });
          })
        )
        .catch((error) => {
          notify(`Error creating transfers. Please try again`, "error", 3000);
          setIsSaving(false);
          setNewVersion(false);
        });
    }
  };

  const renderToastContent = () => {
    return (
      <div
        className='text-center'
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
        }}
      >
        <h4 className='mr-4 text-dark'>Transfers created successfully</h4>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            className='text-uppercase'
            text='Refresh report'
            type='normal'
            stylingMode='contained'
            style={{ marginRight: "10px" }}
            onClick={() => {
              getReport();
              setNewVersion(false);
            }}
          />
          <Button
            text='Close  '
            type='normal'
            stylingMode='outlined'
            onClick={() => setNewVersion(false)}
            style={{
              backgroundColor: "#4a4a4a",
              color: "#ffff",
              marginRight: "10px",
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {isLoading ? <Spinner /> : null}
      {reportStore.length && !isLoading ? (
        <Pagination
          gotoPage={gotoPage}
          isReport
          previousPage={previousPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          nextPage={nextPage}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
        />
      ) : null}
      {!isLoading ? (
        <div
          className='action-container'
          style={reportStore.length ? { marginTop: "20px" } : {}}
        >
          <Button
            type='default'
            stylingMode='outlined'
            text='Create Transfers'
            disabled={!data.length || isSaving}
            onClick={openModal}
          />
          <GlobalFiter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>
      ) : null}

      {!isLoading && reportStore?.length ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
            background: "#0737a0",
            marginTop: "20px",
            width: "40px",
            height: "37px",
            color: "#fff",
            borderRadius: "4px",
            border: "1px solid #0737a0",
            cursor: "pointer",
          }}
          onClick={() => {
            setIsExpanded((prev) => !prev);
            toggleAllRowsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? <MinimizeIcon /> : <ExpandIcon />}
        </div>
      ) : null}

      {!isLoading ? (
        <table {...getTableProps()} className='styled-table  table-bordered'>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <i className='fas fa-arrow-down ml-2'></i>
                        ) : (
                          <i className='fas fa-arrow-up ml-2'></i>
                        )
                      ) : (
                        ""
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment {...row.getRowProps()}>
                  <tr>
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                  {row.isExpanded ? (
                    <tr>
                      <td colSpan={visibleColumns.length}>
                        <SubReportTable
                          row={row}
                          setMain={setMain}
                          mainData={mainData}
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
          {reportStore.length ? (
            <React.Fragment>
              <tfoot>
                {footerGroups.map((footerGroup) => (
                  <tr {...footerGroup.getFooterGroupProps()}>
                    {footerGroup.headers.map((column) => (
                      <td {...column.getFooterProps()}>
                        {column.render("Footer")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            </React.Fragment>
          ) : null}
        </table>
      ) : null}
      {reportStore.length && !isLoading ? (
        <Pagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          nextPage={nextPage}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
        />
      ) : null}
      <VerificationModal
        visible={isOpen}
        toggle={closeModal}
        reports={generatedData}
        isSaving={isSaving}
        createTransfers={createTransfers}
        reportType={reportType}
      />
      <Toast
        visible={newVersionAvailable}
        type='warning'
        width={"50%"}
        displayTime={200000}
        contentRender={renderToastContent}
      />
    </>
  );
};

export default DistributionTable;
